import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { SubjectPerformance } from '../types/testResult';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface SubjectPerformanceChartProps {
  data: SubjectPerformance[];
  height?: number;
}

/**
 * Subject Performance Chart Component
 */
export const SubjectPerformanceChart: React.FC<SubjectPerformanceChartProps> = ({
  data,
  height = 300
}) => {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const subject = data[context.dataIndex];
            return [
              `${context.dataset.label}: ${context.parsed.y}%`,
              `Tests: ${subject.totalTests}`,
              `Questions: ${subject.totalQuestions}`,
              `Correct: ${subject.totalCorrect}`,
              `Wrong: ${subject.totalWrong}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      }
    },
  };

  const getBarColor = (percentage: number) => {
    if (percentage >= 80) return 'rgba(16, 185, 129, 0.8)'; // Green
    if (percentage >= 60) return 'rgba(245, 158, 11, 0.8)'; // Yellow
    return 'rgba(239, 68, 68, 0.8)'; // Red
  };

  const chartData = {
    labels: data.map(item => item.subject),
    datasets: [
      {
        label: 'Average Performance',
        data: data.map(item => item.averagePercentage),
        backgroundColor: data.map(item => getBarColor(item.averagePercentage)),
        borderColor: data.map(item => getBarColor(item.averagePercentage).replace('0.8', '1')),
        borderWidth: 1,
      },
    ],
  };

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500"
        style={{ height: `${height}px` }}
      >
        No subject performance data available
      </div>
    );
  }

  return (
    <div style={{ height: `${height}px` }}>
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
};
